import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import axios from "axios";
import { MessageCircle, Mail, ArrowLeft, Phone, QrCode } from "lucide-react";

const SupportPage = () => {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/landing-settings`);
        setSettings(response.data);
      } catch (error) {
        // Settings are optional here — fall back to defaults
      }
    };
    fetchSettings();
  }, []);

  const openWhatsApp = () => {
    if (settings?.support_whatsapp) {
      const phone = settings.support_whatsapp.replace(/[^0-9]/g, "");
      window.open(`https://wa.me/${phone}`, "_blank");
    }
  };

  const openEmail = () => {
    if (settings?.support_email) {
      window.location.href = `mailto:${settings.support_email}`;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-6 py-8 max-w-lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors"
            data-testid="back-to-home-link"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>

          <div className="text-center mb-8">
            <div className="mx-auto h-16 w-16 rounded-2xl brand-gradient flex items-center justify-center mb-4">
              <QrCode className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold">Contact Support</h1>
            <p className="text-muted-foreground mt-2">
              We're here to help! Reach out to us through any of the channels below.
            </p>
          </div>

          <div className="space-y-4">
            {/* WhatsApp Card */}
            <Card 
              className="cursor-pointer hover:border-green-500/50 transition-all hover:shadow-lg"
              onClick={openWhatsApp}
              data-testid="support-whatsapp-card"
            >
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="h-14 w-14 rounded-2xl bg-green-500 flex items-center justify-center">
                    <MessageCircle className="h-7 w-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">WhatsApp</h3>
                    <p className="text-muted-foreground">
                      {settings?.support_whatsapp || "Not configured"}
                    </p>
                    <p className="text-sm text-green-600 mt-1">Tap to chat</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Email Card */}
            <Card 
              className="cursor-pointer hover:border-primary/50 transition-all hover:shadow-lg"
              onClick={openEmail}
              data-testid="support-email-card"
            >
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="h-14 w-14 rounded-2xl bg-primary flex items-center justify-center">
                    <Mail className="h-7 w-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">Email</h3>
                    <p className="text-muted-foreground">
                      {settings?.support_email || "Not configured"}
                    </p>
                    <p className="text-sm text-primary mt-1">Tap to send email</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* FAQ Section */}
          <div className="mt-12">
            <h2 className="text-xl font-semibold mb-4">Frequently Asked Questions</h2>
            <div className="space-y-4">
              <Card>
                <CardContent className="pt-4">
                  <h3 className="font-medium">How do I create my digital card?</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Simply register on our platform, fill in your profile details, and your digital business card will be ready to share!
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-4">
                  <h3 className="font-medium">How does NFC sharing work?</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Program your NFC card with your profile URL. When someone taps their phone on your card, they'll instantly see your digital profile.
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-4">
                  <h3 className="font-medium">Can I customize my profile appearance?</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Individual users can choose between light and dark themes. Company users inherit their organization's branding for a consistent look.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SupportPage;
